import React, { useContext, useState } from "react";
import { motion } from "framer-motion";
import { CursorContext } from "../context/CursorContext";
import {
  BsHeart,
  BsHeartFill,
  BsBookmark,
  BsBookmarkFill,
  BsShare,
} from "react-icons/bs";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.55, ease: "easeOut" },
  }),
};

const TAG_COLORS = {
  "AI Engineering": { bg: "#8B5CF622", color: "#7C3AED", border: "#8B5CF644" },
  Backend: { bg: "#5EEAD422", color: "#0F766E", border: "#5EEAD444" },
  Frontend: { bg: "#FB718522", color: "#BE123C", border: "#FB718544" },
  Personal: { bg: "#FBBF2422", color: "#B45309", border: "#FBBF2444" },
};

const BlogContent = ({ blog, index, total }) => {
  const { mouseEnterHandler, mouseLeaveHandler } = useContext(CursorContext);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [likeCount, setLikeCount] = useState(blog.likes);
  const tagStyle = TAG_COLORS[blog.tag] || TAG_COLORS["Personal"];
  const num = String(index + 1).padStart(2, "0");

  const handleLike = () => {
    setLiked((prev) => !prev);
    setLikeCount((prev) => (liked ? prev - 1 : prev + 1));
  };

  return (
    <section
      id={`blog-${index}`}
      className="h-full snap-start relative flex flex-col bg-beige dark:bg-mate overflow-hidden"
      onMouseEnter={mouseEnterHandler}
      onMouseLeave={mouseLeaveHandler}
    >
      {/* ── Full-bleed image (top half) ── */}
      <div className="relative w-full flex-[0_0_52%] overflow-hidden">
        <motion.img
          src={blog.image}
          alt={blog.title}
          className="w-full h-full object-cover"
          initial={{ scale: 1.06 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: false, amount: 0.5 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
        />

        {/* gradient fade into body */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-beige dark:from-mate to-transparent" />

        {/* post counter top-left (below header) */}
        <div className="absolute top-3 left-4 sm:left-8 flex items-center gap-2">
          <span className="font-primary font-bold text-white text-base drop-shadow">
            {num}
          </span>
          <span className="text-white/50 text-xs drop-shadow">
            / {String(total).padStart(2, "0")}
          </span>
        </div>

        {/* tag badge top-right */}
        <div className="absolute top-3 right-4 sm:right-8">
          <span
            className="text-[11px] font-semibold px-3 py-1 rounded-full"
            style={{
              backgroundColor: tagStyle.bg,
              color: tagStyle.color,
              border: `1px solid ${tagStyle.border}`,
              backdropFilter: "blur(8px)",
            }}
          >
            {blog.tag}
          </span>
        </div>
      </div>

      {/* ── Post body (bottom half) ── */}
      <div className="flex-1 flex flex-col justify-between px-4 sm:px-8 lg:px-16 pt-4 pb-6 overflow-hidden max-w-3xl mx-auto w-full">
        {/* meta */}
        <motion.div
          className="flex items-center gap-3 mb-2"
          variants={fadeUp}
          custom={0}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
        >
          <span className="text-[11px] text-slate-400 uppercase tracking-widest">
            {blog.date}
          </span>
          <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600" />
          <span className="text-[11px] text-slate-400 uppercase tracking-widest">
            {blog.readTime}
          </span>
        </motion.div>

        {/* title */}
        <motion.h2
          className="font-primary font-bold text-xl sm:text-2xl lg:text-3xl leading-snug mb-3"
          variants={fadeUp}
          custom={1}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
        >
          {blog.title}
        </motion.h2>

        {/* excerpt */}
        <motion.p
          className="text-sm sm:text-base leading-relaxed text-slate-500 dark:text-slate-400 line-clamp-4 flex-1"
          variants={fadeUp}
          custom={2}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
        >
          {blog.excerpt}
        </motion.p>

        {/* ── Action bar ── */}
        <motion.div
          className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200 dark:border-slate-700"
          variants={fadeUp}
          custom={3}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
        >
          {/* left: like + save */}
          <div className="flex items-center gap-5">
            {/* like */}
            <button
              onClick={handleLike}
              className="flex items-center gap-2 group"
              aria-label="Like post"
            >
              <motion.span
                animate={liked ? { scale: [1, 1.35, 1] } : { scale: 1 }}
                transition={{ duration: 0.3 }}
                className={`text-lg ${liked ? "text-rose-500" : "text-slate-400 group-hover:text-rose-400 transition-colors"}`}
              >
                {liked ? <BsHeartFill /> : <BsHeart />}
              </motion.span>
              <span className="text-xs font-medium text-slate-400">
                {likeCount}
              </span>
            </button>

            {/* save */}
            <button
              onClick={() => setSaved((p) => !p)}
              className="flex items-center gap-1 group"
              aria-label="Save post"
            >
              <span
                className={`text-lg ${saved ? "text-violet-500" : "text-slate-400 group-hover:text-violet-400 transition-colors"}`}
              >
                {saved ? <BsBookmarkFill /> : <BsBookmark />}
              </span>
            </button>
          </div>

          {/* right: read more */}
          <div className="flex items-center gap-4">
            <button
              className="flex items-center gap-1 text-slate-400 hover:text-violet-500 transition-colors"
              aria-label="Share"
              onClick={() => {
                if (navigator.share) {
                  navigator.share({ title: blog.title, text: blog.excerpt });
                }
              }}
            >
              <BsShare className="text-base" />
            </button>

            <motion.button
              whileHover={{ x: 4 }}
              className="text-xs font-semibold uppercase tracking-widest text-violet-500 hover:text-violet-600 transition-colors"
            >
              Read full post →
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* ── Scroll hint on first post ── */}
      {index === 0 && (
        <motion.div
          className="absolute bottom-3 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 pointer-events-none"
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: [0, 1, 0], y: [0, 6, 0] }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1.5,
          }}
        >
          <span className="text-[10px] uppercase tracking-widest text-slate-400">
            scroll
          </span>
          <div className="w-px h-5 bg-slate-300 dark:bg-slate-600" />
        </motion.div>
      )}
    </section>
  );
};

export default BlogContent;
